import Fraction from 'fraction.js'
import pluralize from 'pluralize'
['c', 'fl. oz', 'mg', 'g', 'kg', 'lb', 'ml', 'l', 'L', 'oz', 'tsp', 'tbsp'].forEach(unit => {
  pluralize.plural(unit)
  pluralize.addUncountableRule(unit)
})
const SYSTEMS = [
  'default',
  'customary',
  'metric'
]
const UNITS = {
  g: {
    conversions: {
      customary: {
        factor: 0.035274,
        unit: 'oz'
      }
    },
    simplify: (measure) => {
      if (measure.quantity.valueOf() >= 1000) {
        return {
          factor: 0.001,
          unit: 'kg'
        }
      } else if (measure.quantity.valueOf() <= 0.001) {
        return {
          factor: 1000,
          unit: 'mg'
        }
      }
    },
    uncountable: true,
    system: 'metric'
  },
  kg: {
    conversions: {
      customary: {
        factor: 2.20462,
        unit: 'lb'
      }
    },
    simplify: (measure) => {
      if (measure.quantity.valueOf() < 0.5) {
        return {
          factor: 1000,
          unit: 'g'
        }
      }
    },
    uncountable: true,
    system: 'metric'
  },
  'fl oz': {
    conversions: {
      metric: {
        factor: 29.5, // 29.5735
        unit: 'ml'
      }
    },
    uncountable: true,
    system: 'customary'
  },
  pint: {
    conversions: {
      metric: {
        factor: 473, // 473.176
        unit: 'ml'
      }
    },
    uncountable: true,
    system: 'customary'
  },
  lb: {
    conversions: {
      metric: {
        factor: 454, // 453.592,
        unit: 'g'
      }
    },
    simplify: (measure) => {
      if (measure.quantity.valueOf() < 1) {
        return {
          factor: 16,
          unit: 'oz'
        }
      }
    },
    uncountable: true,
    system: 'customary'
  },
  oz: {
    conversions: {
      metric: {
        factor: 28, // 28.3495,
        unit: 'g'
      }
    },
    uncountable: true,
    system: 'customary'
  },
  inch: {
    conversions: {
      metric: {
        factor: 2.54,
        unit: 'cm'
      }
    },
    uncountable: true,
    system: 'customary'
  },
  tbsp: {
    conversions: {
      metric: {
        factor: 1,
        unit: 'tbsp'
      }
    },
    simplify: (measure) => {
      if (measure.quantity.valueOf() < 0.5) {
        return {
          factor: 3,
          unit: 'tsp'
        }
      }
      if (measure.quantity.valueOf() > 8) {
        return {
          factor: 0.0625,
          unit: 'cup'
        }
      }
    },
    uncountable: true,
    system: 'customary'
  },
  tsp: {
    conversions: {
      metric: {
        factor: 1,
        unit: 'tsp'
      }
    },
    simplify: (measure) => {
      if (measure.quantity.valueOf() > 6) {
        return {
          factor: 0.333,
          unit: 'tbsp'
        }
      }
    },
    uncountable: true,
    system: 'customary'
  }
}
export default class Measure {
  constructor ({ dimension, quantity, rangeEnd, unit = 'each', modifier, modifierType }) {
    this._dimension = dimension
    this._quantity = quantity
    this._rangeEnd = rangeEnd
    this._unit = unit
    this._modifier = modifier
    this._modifierType = modifierType
    this._excludeWhole = true

    if (UNITS[this._unit]) {
      this._system = UNITS[this._unit].system
    }
  }

  get dimension () {
    return this._dimension
  }

  get modifier () {
    return this._modifier
  }

  get presentationModifier () {
    if (!this.modifier) return
    switch (this._modifierType) {
      case 'uom': return `(${this._modifier})`
      default: return this._modifier
    }
  }

  get quantity () {
    // if (!this._quantity) return undefined
    return Fraction(this._quantity)
  }

  get quantityPreferFixed () {
    return this.quantity.d > 6
  }

  get rangeEnd () {
    if (!this._rangeEnd) return undefined
    return Fraction(this._rangeEnd)
  }

  get rangeEndPreferFixed () {
    return this._rangeEnd && this._rangeEnd.d > 6
  }

  formatFraction (fraction, format = 'fraction') {
    if (format === 'fixed') {
      let decimalPlaces
      switch (fraction.d) {
        case 1:
          decimalPlaces = 0; break
        case 4:
          decimalPlaces = 2; break
        default:
          decimalPlaces = Math.max(1, fraction.d.toString().length - 2)
      }
      return fraction.valueOf().toFixed(decimalPlaces)
    } else {
      return fraction.toFraction(this._excludeWhole)
    }
  }

  get presentationQuantity () {
    let fixedPrefer = this.quantityPreferFixed || this.rangeEndPreferFixed
    let format = fixedPrefer ? 'fixed' : 'fraction'
    let parts = []
    parts.push(this.formatFraction(this.quantity, format))
    if (this.rangeEnd) {
      parts.push(this.formatFraction(this.rangeEnd, format))
    }
    return parts.join('–')
  }

  get unit () {
    return this._unit
  }

  get presentationUnit () {
    let quantity = this.quantity.valueOf()
    // return (this.quantity <= 1) ? unit : pluralize(unit, quantity)
    return pluralize(this._unit, quantity)
  }

  scale (scale = '1') {
    return new Measure({
      dimension: this._dimension,
      quantity: this._quantity ? this.quantity.mul(scale) : undefined,
      rangeEnd: this._rangeEnd ? this.rangeEnd.mul(scale) : undefined,
      unit: this._unit,
      modifier: this._modifier,
      modifierType: this._modifierType
    })
  }

  convert (system = 'default') {
    if (SYSTEMS.indexOf(system) === -1) {
      throw new Error('Unknown system of measure')
    }

    if (system === 'default') return this
    if (system === this._system) return this
    if (!UNITS[this._unit]) return this

    let conv = UNITS[this._unit].conversions[system]

    return this.transform(conv)
  }

  simplify () {
    if (!UNITS[this._unit] || !UNITS[this._unit].simplify) return this
    let conv = UNITS[this._unit].simplify(this)
    if (!conv) return this

    return new Measure({
      dimension: this._dimension,
      quantity: this._quantity ? this.quantity.mul(conv.factor) : undefined,
      rangeEnd: this._rangeEnd ? this.rangeEnd.mul(conv.factor) : undefined,
      unit: conv.unit,
      modifier: this._modifier,
      modifierType: this._modifierType
    })
  }

  transform ({ unit, factor }) {
    return new Measure({
      dimension: this._dimension,
      quantity: this._quantity ? this.quantity.mul(factor) : undefined,
      rangeEnd: this._rangeEnd ? this.rangeEnd.mul(factor) : undefined,
      unit: unit,
      modifier: this._modifier,
      modifierType: this._modifierType
    })
  }

  toString () {
    let parts = [this.presentationQuantity]
    if (this._modifier) parts.push(this.presentationModifier)
    if (this._unit !== 'each') parts.push(this.presentationUnit)

    return parts.join('&nbsp;')
  }
}



// WEBPACK FOOTER //
// ./src/models/Measure.js